import { Button, Form, Input, message } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useTranslation } from '../../i18n/i18n';
import './ResetPasswordForm.scss';
import { useLocation, useNavigate } from 'react-router-dom';
import { authApi } from '../../api/auth.api';
import { dashboardPath, devicesPath } from '../../routes';
import useAuthStore from '../../stores/auth.store';

const passwordRules = (t: (key: string) => string) => [
  { required: true },
  { min: 10, message: t('passwordValidation.minLength') },
  { pattern: /[A-Z]/, message: t('passwordValidation.uppercase') },
  { pattern: /[a-z]/, message: t('passwordValidation.lowercase') },
  { pattern: /[0-9]/, message: t('passwordValidation.number') },
  { pattern: /[^A-Za-z0-9]/, message: t('passwordValidation.specialChar') },
];

interface FormValues {
  newPassword: string;
  confirmPassword: string;
}

const ResetPasswordForm = () => {
  const [form] = Form.useForm<FormValues>();
  const [messageApi, contextHolder] = message.useMessage();
  const { t } = useTranslation('auth', { keyPrefix: 'restorePassword' });
  const { t: tAcc } = useTranslation('account');
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuthStore((s) => s.login);
  const state = location.state as { email: string; temporaryPassword: string };

  const onFinish = async (values: FormValues) => {
    try {
      await authApi.completeTemporaryPassword({
        email: state.email,
        temporaryPassword: state.temporaryPassword,
        newPassword: values.newPassword,
      });
      await login(state.email, values.newPassword);
      navigate(`/${dashboardPath}/${devicesPath}`, { replace: true });
    } catch {
      messageApi.error(t('common:errors.generic'));
    }
  };
  return (
    <div>
      {contextHolder}
      <Form<FormValues> form={form} name="login" onFinish={onFinish} className={'ResetPasswordForm'}>
        {/* New password */}
        <Form.Item name="newPassword" rules={passwordRules(tAcc)}>
          <Input.Password
            prefix={<LockOutlined className="ResetPasswordForm__siteFormItemIcon" />}
            placeholder={t('newPasswordPlaceholder')}
          />
        </Form.Item>

        {/* Confirm Password */}
        <Form.Item
          name="confirmPassword"
          rules={[
            { required: true },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(tAcc('passwordValidation.noMatch')));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined className="ResetPasswordForm__siteFormItemIcon" />}
            type="password"
            placeholder={t('confirmPasswordPlaceholder')}
          />
        </Form.Item>

        {/* Submit */}
        <Form.Item>
          <Button type="primary" htmlType="submit" className="ResetPasswordForm__loginFormButton" block>
            {t('restorePassword')}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default ResetPasswordForm;
